// 📄 pages/Proyectos/components/TareaFormModal.tsx
// Alta y edición de una tarea — el asignado puede ser CUALQUIER miembro
// real del proyecto, incluido el rol "cliente" (ver Fase 0 de Tareas: un
// cliente puede tener una dependencia real hacia el equipo).
import React, { useState } from 'react';
import { X, Save, RefreshCw } from 'lucide-react';
import { ProjectMember } from '../../../services/projectService.ts';
import { Tarea, TareaFormData, createTarea, updateTareaDatos } from '../../../services/tareaService.ts';
import { Hito } from '../../../services/hitoService.ts';

interface TareaFormModalProps {
  projectId: string;
  proyectoNombre: string;
  miembros: ProjectMember[];
  tarea?: Tarea | null; // presente = edición
  creadoPor: string;
  creadoPorNombre: string;
  // Hitos del proyecto para asociar la tarea (opcional) y uno preseleccionado
  // al crearla desde un hito (ver HitosSection.tsx).
  hitos?: Hito[];
  hitoInicial?: string;
  // Título sugerido al crear la tarea desde otra sección (ej. una solicitud de cambio aprobada).
  tituloInicial?: string;
  onClose: () => void;
  onSaved: () => void;
}

const emptyForm = (miembros: ProjectMember[], hitoId = ''): TareaFormData => ({
  titulo: '', descripcion: '', asignadoA: miembros[0]?.userId || '', fechaLimite: '', seccionRelacionada: '', hitoId
});

export default function TareaFormModal({ projectId, proyectoNombre, miembros, tarea, creadoPor, creadoPorNombre, hitos = [], hitoInicial = '', tituloInicial = '', onClose, onSaved }: TareaFormModalProps) {
  const [form, setForm] = useState<TareaFormData>(
    tarea
      ? { titulo: tarea.titulo, descripcion: tarea.descripcion, asignadoA: tarea.asignadoA, fechaLimite: tarea.fechaLimite, seccionRelacionada: tarea.seccionRelacionada, hitoId: tarea.hitoId || '' }
      : { ...emptyForm(miembros, hitoInicial), titulo: tituloInicial }
  );
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setGuardando(true);
    const hitoNombre = hitos.find((h) => h.id === form.hitoId)?.nombre || '';
    try {
      if (tarea) {
        await updateTareaDatos(projectId, proyectoNombre, tarea.id, form, miembros, tarea.asignadoA, hitoNombre);
      } else {
        await createTarea(projectId, proyectoNombre, form, miembros, creadoPor, creadoPorNombre, hitoNombre);
      }
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Error al guardar la tarea.');
    } finally {
      setGuardando(false);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-medium text-white">{tarea ? 'Editar tarea' : 'Nueva tarea'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto flex-1">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          <div>
            <label className={labelClass}>Título*</label>
            <input
              type="text" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              placeholder="ej. Dar accesos al sistema a los consultores" className={inputClass}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Asignado a*</label>
              <select value={form.asignadoA} onChange={(e) => setForm({ ...form, asignadoA: e.target.value })} className={inputClass}>
                {miembros.map((m) => <option key={m.userId} value={m.userId}>{m.email}{m.rol === 'cliente' ? ' (cliente)' : ''}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Fecha límite*</label>
              <input type="date" value={form.fechaLimite} onChange={(e) => setForm({ ...form, fechaLimite: e.target.value })} className={inputClass} />
            </div>
          </div>

          {hitos.length > 0 && (
            <div>
              <label className={labelClass}>Hito (opcional)</label>
              <select value={form.hitoId || ''} onChange={(e) => setForm({ ...form, hitoId: e.target.value })} className={inputClass}>
                <option value="">Sin hito</option>
                {hitos.map((h) => <option key={h.id} value={h.id}>{h.nombre}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className={labelClass}>Sección relacionada (opcional)</label>
            <input
              type="text" value={form.seccionRelacionada} onChange={(e) => setForm({ ...form, seccionRelacionada: e.target.value })}
              placeholder="ej. Configuraciones Generales, SLP..." className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Descripción</label>
            <textarea
              value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              rows={3} className={`${inputClass} resize-none`} placeholder="Detalle de qué hay que hacer..."
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all">
              Cancelar
            </button>
            <button
              type="submit" disabled={guardando}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
            >
              {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {guardando ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
